import { cva } from 'class-variance-authority';
import type { PillColorsEnum } from '../types';

const pillVariants = cva(
  'border w-fit max-w-full border-box flex flex-row shrink-0 justify-center items-center border-secondary',
  {
    variants: {
      variant: {
        soft: '',
        clear: 'border-secondary bg-primary',
        solid: 'text-white border-none',
        subtle: '',
        ghost: 'border-dashed  bg-primary',
        outlined: 'border-solid bg-primary',
      },
      color: {
        gray: '',
        brand: '',
        error: '',
        warning: '',
        success: '',
        blue: '',
        purple: '',
        fuchsia: '',
        cyan: '',
        lime: '',
        pink: '',
        violet: '',
        indigo: '',
        rose: '',
        yellow: '',
        blueDark: '',
      } satisfies Record<PillColorsEnum, string>,
      shape: {
        rounded: 'rounded-full py-xs',
        square: 'rounded-sm py-xs',
        sharp: 'rounded-xxs py-xs',
      },
      size: {
        md: '!p-md h-fit',
        sm: 'px-md h-6',
        xs: 'px-sm h-5',
        // 16px count pill from the design system (Pill squared) — fixed height, so shape's py is dropped
        xxs: 'px-xs !py-none h-4',
      },
      disabled: {
        true: 'opacity-50',
      },
    },
    compoundVariants: [
      {
        // Figma "Pill squared" spec: xxs uses radius-xs (4px), not the square shape's radius-sm
        size: 'xxs',
        shape: 'square',
        className: '!rounded-xs',
      },
      {
        variant: 'soft',
        color: 'gray',
        className: 'border-utility-gray-100 bg-utility-gray-50 is-platform:bg-utility-gray-100',
      },
      {
        variant: 'soft',
        color: 'brand',
        className:
          'border-utility-brand-100 bg-utility-brand-50 is-platform:bg-[var(--palette-brand-25)]',
      },
      {
        variant: 'soft',
        color: 'error',
        className: 'border-utility-error-100 bg-utility-error-50 is-platform:bg-utility-error-25',
      },
      {
        variant: 'soft',
        color: 'warning',
        className:
          'border-utility-warning-100 bg-utility-warning-50 is-platform:bg-utility-warning-25',
      },
      {
        variant: 'soft',
        color: 'success',
        className:
          'border-utility-success-100 bg-utility-success-50 is-platform:bg-utility-success-25',
      },
      {
        variant: 'soft',
        color: 'blue',
        className: 'border-utility-blue-100 bg-utility-blue-50 is-platform:bg-utility-blue-25',
      },
      {
        variant: 'soft',
        color: 'purple',
        className:
          'border-utility-purple-100 bg-utility-purple-50 is-platform:bg-utility-purple-25',
      },
      {
        variant: 'soft',
        color: 'fuchsia',
        className:
          'border-utility-fuchsia-100 bg-utility-fuchsia-50 is-platform:bg-utility-fuchsia-25',
      },
      {
        variant: 'soft',
        color: 'cyan',
        className: 'border-utility-cyan-100 bg-utility-cyan-50 is-platform:bg-utility-cyan-25',
      },
      {
        variant: 'soft',
        color: 'lime',
        className: 'border-utility-lime-100 bg-utility-lime-50 is-platform:bg-utility-lime-25',
      },
      {
        variant: 'soft',
        color: 'pink',
        className: 'border-utility-pink-100 bg-utility-pink-50 is-platform:bg-utility-pink-25',
      },
      {
        variant: 'soft',
        color: 'violet',
        className:
          'border-utility-violet-100 bg-utility-violet-50 is-platform:bg-utility-violet-25',
      },
      {
        variant: 'soft',
        color: 'indigo',
        className:
          'border-utility-indigo-100 bg-utility-indigo-50 is-platform:bg-utility-indigo-25',
      },
      {
        variant: 'soft',
        color: 'rose',
        className: 'border-utility-rose-100 bg-utility-rose-50 is-platform:bg-utility-rose-25',
      },
      {
        variant: 'soft',
        color: 'blueDark',
        className:
          'border-utility-blue-dark-100 bg-utility-blue-dark-50 is-platform:bg-utility-blue-dark-25',
      },
      {
        variant: 'solid',
        color: 'brand',
        className: 'bg-utility-brand-600',
      },
      {
        variant: 'solid',
        color: 'gray',
        className: 'bg-utility-gray-600',
      },
      {
        variant: 'solid',
        color: 'error',
        className: 'bg-utility-error-600 is-platform:bg-utility-error-500',
      },
      {
        variant: 'solid',
        color: 'warning',
        className: 'bg-utility-warning-600 is-platform:bg-utility-warning-500',
      },
      {
        variant: 'solid',
        color: 'success',
        className: 'bg-utility-success-600 is-platform:bg-utility-success-500',
      },
      {
        variant: 'solid',
        color: 'blue',
        className: 'bg-utility-blue-600',
      },
      {
        variant: 'solid',
        color: 'purple',
        className: 'bg-utility-purple-600',
      },
      {
        variant: 'solid',
        color: 'fuchsia',
        className: 'bg-utility-fuchsia-600',
      },
      {
        variant: 'solid',
        color: 'cyan',
        className: 'bg-utility-cyan-600 is-platform:bg-utility-cyan-500',
      },
      {
        variant: 'solid',
        color: 'lime',
        className: 'bg-utility-lime-600',
      },
      {
        variant: 'solid',
        color: 'pink',
        className: 'bg-utility-pink-600',
      },
      {
        variant: 'solid',
        color: 'violet',
        className: 'bg-utility-violet-600 is-platform:bg-utility-violet-500',
      },
      {
        variant: 'solid',
        color: 'indigo',
        className: 'bg-utility-indigo-600',
      },
      {
        variant: 'solid',
        color: 'rose',
        className: 'bg-utility-rose-600',
      },
      {
        variant: 'solid',
        color: 'blueDark',
        className: 'bg-utility-blue-dark-600 is-platform:bg-utility-blue-dark-500',
      },
      {
        variant: 'solid',
        color: 'yellow',
        className: 'bg-utility-warning-300',
      },
      {
        variant: 'subtle',
        color: 'gray',
        className: 'border-none bg-utility-gray-50',
      },
      {
        variant: 'subtle',
        color: 'brand',
        className: 'border-none bg-utility-brand-50',
      },
      {
        variant: 'subtle',
        color: 'error',
        className: 'border-none bg-utility-error-50',
      },
      {
        variant: 'subtle',
        color: 'warning',
        className: 'border-none bg-utility-warning-50',
      },
      {
        variant: 'subtle',
        color: 'success',
        className: 'border-none bg-utility-success-50',
      },
      {
        variant: 'subtle',
        color: 'blue',
        className: 'border-none bg-utility-blue-50',
      },
      {
        variant: 'subtle',
        color: 'purple',
        className: 'border-none bg-utility-purple-50',
      },
      {
        variant: 'subtle',
        color: 'fuchsia',
        className: 'border-none bg-utility-fuchsia-50',
      },
      {
        variant: 'subtle',
        color: 'cyan',
        className: 'border-none bg-utility-cyan-50',
      },
      {
        variant: 'subtle',
        color: 'lime',
        className: 'border-none bg-utility-lime-50',
      },
      {
        variant: 'subtle',
        color: 'pink',
        className: 'border-none bg-utility-pink-50',
      },
      {
        variant: 'subtle',
        color: 'violet',
        className: 'border-none bg-utility-violet-50',
      },
      {
        variant: 'subtle',
        color: 'indigo',
        className: 'border-none bg-utility-indigo-50',
      },
      {
        variant: 'subtle',
        color: 'rose',
        className: 'border-none bg-utility-rose-50',
      },
      {
        variant: 'ghost',
        color: 'gray',
        className: 'border-utility-gray-100',
      },
      {
        variant: 'ghost',
        color: 'brand',
        className: 'border-utility-brand-100',
      },
      {
        variant: 'ghost',
        color: 'error',
        className: 'border-utility-error-100',
      },
      {
        variant: 'ghost',
        color: 'warning',
        className: 'border-utility-warning-100',
      },
      {
        variant: 'ghost',
        color: 'success',
        className: 'border-utility-success-100',
      },
      {
        variant: 'ghost',
        color: 'blue',
        className: 'border-utility-blue-100',
      },
      {
        variant: 'ghost',
        color: 'purple',
        className: 'border-utility-purple-100',
      },
      {
        variant: 'ghost',
        color: 'fuchsia',
        className: 'border-utility-fuchsia-100',
      },
      {
        variant: 'ghost',
        color: 'cyan',
        className: 'border-utility-cyan-100',
      },
      {
        variant: 'ghost',
        color: 'lime',
        className: 'border-utility-lime-100',
      },
      {
        variant: 'ghost',
        color: 'pink',
        className: 'border-utility-pink-100',
      },
      {
        variant: 'ghost',
        color: 'violet',
        className: 'border-utility-violet-100',
      },
      {
        variant: 'ghost',
        color: 'indigo',
        className: 'border-utility-indigo-100',
      },
      {
        variant: 'ghost',
        color: 'rose',
        className: 'border-utility-rose-100',
      },
      {
        variant: 'outlined',
        color: 'gray',
        className: 'border-utility-gray-700',
      },
      {
        variant: 'outlined',
        color: 'brand',
        className: 'border-utility-brand-700',
      },
      {
        variant: 'outlined',
        color: 'error',
        className: 'border-utility-error-700',
      },
      {
        variant: 'outlined',
        color: 'warning',
        className: 'border-utility-warning-700',
      },
      {
        variant: 'outlined',
        color: 'success',
        className: 'border-utility-success-700',
      },
      {
        variant: 'outlined',
        color: 'blue',
        className: 'border-utility-blue-700',
      },
      {
        variant: 'outlined',
        color: 'purple',
        className: 'border-utility-purple-700',
      },
      {
        variant: 'outlined',
        color: 'fuchsia',
        className: 'border-utility-fuchsia-700',
      },
      {
        variant: 'outlined',
        color: 'cyan',
        className: 'border-utility-cyan-700',
      },
      {
        variant: 'outlined',
        color: 'lime',
        className: 'border-utility-lime-700',
      },
      {
        variant: 'outlined',
        color: 'pink',
        className: 'border-utility-pink-700',
      },
      {
        variant: 'outlined',
        color: 'violet',
        className: 'border-utility-violet-700',
      },
      {
        variant: 'outlined',
        color: 'indigo',
        className: 'border-utility-indigo-700',
      },
      {
        variant: 'outlined',
        color: 'rose',
        className: 'border-utility-rose-700',
      },
      {
        variant: 'outlined',
        color: 'yellow',
        className: 'border-utility-warning-300',
      },
    ],
    defaultVariants: {},
  },
);

export default pillVariants;
